import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "./style.css";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { Tooltip } from "react-tooltip";
import { isMobile } from "react-device-detect";
import ChatModal from "../../Components/ChatModal/ChatModal";
import SummaryModal from "./modalComponent/summary/SummaryModal";
import MobilePopUp from "../../Components/MobilePopUp/MobilePopUp";
import ActionPlanModal from "./modalComponent/actionPlan/ActionPlanModal";
import History from "./history";
import { RealtimeClient } from "../../Components/realtime-api-beta/index";
import { startDialogue } from "../../Services/dialogues";
import BrowserOpusDecoder from "../../Components/realtime-api-beta/lib/opusDecoder";
import { WavRecorder, WavStreamPlayer48k } from "../../Components/wavtools/index";
import SmartHabit from "./modalComponent/smartHabit/SmartHabit";
import DefaultLoader from "../LoadingPage/DefaultLoader";
import { endSession, fetchGroupInstructions, getChat, handleStoreVoiceEmbedding } from "../../Services/coaching";
import Cookies from "js-cookie";
import VideoDisplay from "./components/VideoDisplay";
import Header from "./components/Header";
import SpeechControls from "./components/SpeechControls";
import TimeoutBanner from "./components/TimeoutBanner";
import StatusMessage from "./components/StatusMessage";
import TagButton from "./components/TagButton";
import ExitModal from "./components/ExitModal";
import InactivityModal from "./components/InactivityModal";
import { getUserFaith } from "../../Services/subscription";
import { getOpenAIVoice } from "../../Utils/CommonFunctions/voiceMapping";
import { v4 as uuidv4 } from "uuid";
import { MicVAD } from "@ricky0123/vad-web";
import VoiceChatHistory from "../RelationShip/VoiceChatHistory";
import RequestCoachingMinutes from "../RelationShip/Dialogues/RequestCoachingMinutes";
import { updateCoachingMode, setVoiceWalkthrough } from "../../Services/userSettings";
import { getUserProfile } from "../../Services/MyProfile";
import {
  isActivationPending,
  getCompletedStepsFromStorage,
} from "../../Utils/onboardingNavigation";
import useActivationCompletion from "../../Hooks/useActivationCompletion";
import { showSuccessToast } from "../../Utils/toast";
import { trackEvent } from "../../Services/amplitude";
import { MISSION_EVENT, missionRoutes } from "../Journey/Mission/evolution";
import ArisTour from "../../Components/Tours/ArisTour";
import { getWelcomeMessage } from "../../Services/welcomeMessge";
import GenericModal from "../../Components/Modal/GenericModal";
import { CoachingTopicsModal, TopicOptionsModal } from "../../Components/CoachingTopics";
import { YourElementsModal, YourElementsEntryButton } from "../../Components/YourElements";
import { useFunctionalityAccess } from "../../Providers/FunctionalityAccessProvider";
import { ReactComponent as BackArrowIcon } from "../../Assets/icon/arrow-narrow-left.svg";
import { ReactComponent as CalmDownIcon } from "../../Assets/icon/calm-down.svg";
import { ReactComponent as RelationshipsIcon } from "../../Assets/icon/relationships.svg";
import { ReactComponent as MeditationIcon } from "../../Assets/icon/meditation_icon.svg";

// Motivate Me Icon for voice coaching display
const MotivateIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" fill="none">
    <path d="M25.0001 32.2917H15.6251C12.7177 32.2917 11.2639 32.2917 10.081 32.6505C7.4177 33.4584 5.3335 35.5426 4.52558 38.206C4.16675 39.3889 4.16675 40.8426 4.16675 43.75M33.3334 37.5L37.5001 41.6667L45.8334 33.3333M30.2084 15.625C30.2084 20.8027 26.0111 25 20.8334 25C15.6557 25 11.4584 20.8027 11.4584 15.625C11.4584 10.4473 15.6557 6.25 20.8334 6.25C26.0111 6.25 30.2084 10.4473 30.2084 15.625Z" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Get icon component based on category/topic for voice coaching display
const getTopicIcon = (categoryName, topicTitle) => {
  const lowerCategory = (categoryName || '').toLowerCase();
  const lowerTopic = (topicTitle || '').toLowerCase();

  if (lowerTopic.includes('calm') || lowerCategory.includes('calm') || lowerCategory.includes('mental')) {
    return CalmDownIcon;
  }
  if (lowerTopic.includes('motivat') || lowerCategory.includes('motivat') || lowerCategory.includes('physical')) {
    return MotivateIcon;
  }
  if (lowerTopic.includes('relationship') || lowerCategory.includes('relationship')) {
    return RelationshipsIcon;
  }
  return MeditationIcon;
};

/**
 * @param {boolean} autoStart Open the voice session as soon as entitlements have
 *   resolved, instead of waiting for the Start control. Set by the Aris tab so
 *   choosing "Voice" is one tap; the /coach routes leave it off.
 */
function ArisCoach({ autoStart = false }) {
  const { setHeaderContent, setIsCoachingActive, profileContent } = useHeader();
  const { hasUnlimitedCoaching, getCoachingMinutes, initialized: accessInitialized, access } = useFunctionalityAccess();
  const navigate = useNavigate();
  const location = useLocation();

  const user_ids = new URLSearchParams(location.search).get('user_ids');
  const relation_ids = new URLSearchParams(location.search).get('relation_ids');
  const isGroupCoaching = location.pathname === '/group-coach' || !!user_ids || !!relation_ids;

  // Resume handoff: a previous individual voice conversation to continue.
  // Captured once on mount so it stays stable as the user starts the session.
  // Only honored for individual coaching (see applyResumeToClient).
  const resumeConversationId = new URLSearchParams(location.search).get('resume_conversation_id');

  // ARIS Dialogue handoff: /coach/private?dialogue_id=<template>. The instance
  // is prepared in the background from mount (startDialogue runs LLM
  // personalization for tens of seconds), and connectConversation awaits it so
  // the WebSocket handshake carries dialogue_instance_id.
  //
  // Continuing an existing Dialogue: /coach/private?dialogue_instance_id=<id>
  // (the Active rail, or a Detail whose primary_action is CONTINUE_DIALOGUE).
  // There is nothing to prepare — calling startDialogue would open a second
  // instance — so the promise resolves straight to the known instance.
  // Only the instance id rides the handshake: the server picks the session
  // and continues the Dialogue's one conversation, text turns included.
  const dialogueTemplateId = new URLSearchParams(location.search).get('dialogue_id');
  const continuedInstanceId = new URLSearchParams(location.search).get('dialogue_instance_id');
  const dialogueInstancePromiseRef = useRef(null);
  useEffect(() => {
    if (dialogueInstancePromiseRef.current) return;
    if (continuedInstanceId) {
      dialogueInstancePromiseRef.current = Promise.resolve({ instance_id: continuedInstanceId });
      return;
    }
    if (!dialogueTemplateId) return;
    dialogueInstancePromiseRef.current = startDialogue(dialogueTemplateId).catch(
      (e) => {
        console.error('[Coach] failed to prepare the dialogue instance', e);
        return null;
      }
    );
  }, [dialogueTemplateId, continuedInstanceId]);
  const isDialogueHandoff = !!dialogueTemplateId || !!continuedInstanceId;

  // Daily Check-In v26.1 (guide §5.1b): /coach/private?checkin_session_id=<id>
  // from the Journey card's Voice choice. The id rides the WebSocket handshake
  // and the server attaches the check-in brief; it completes the check-in at
  // teardown when the member said enough — hanging up is never a completion.
  // The member already chose Voice, so the session opens straight away.
  const checkinSessionId = new URLSearchParams(location.search).get('checkin_session_id');
  const isCheckInHandoff = !!checkinSessionId;

  // "Talk with Aris" from a Commitment detail (UD v26.5 C7):
  // /coach/private?commitment_id=<id>. The id rides the handshake and the
  // server frames the session with that commitment (owner-scoped).
  const commitmentId = new URLSearchParams(location.search).get('commitment_id');
  // Handoffs where the member already picked Voice on a sheet: open at once,
  // and go back where they came from when the session ends.

  // Mission evolution (Journey v26.3): /coach/private?dialog_type=
  // mission_evolution&evolution_session_id=<id>, from the Evolve page's Voice
  // choice. The server sends a `mission_evolution` frame when Aris has
  // proposed an evolved Mission (mission_draft_ready, once) or concluded it
  // still fits (mission_evolution_closed). Nothing changes until the member
  // saves on the Review screen, which opens when the call ends.
  const missionEvolutionId = new URLSearchParams(location.search).get('evolution_session_id');
  const missionDraftIdRef = useRef(null);
  const [missionDraftReady, setMissionDraftReady] = useState(false);
  const handleMissionEvolutionFrame = (event) => {
    const content = event?.content || {};
    if (content.event === 'mission_draft_ready' && content.draft_id) {
      if (missionDraftIdRef.current !== content.draft_id) {
        trackEvent(MISSION_EVENT.DRAFT_GENERATED, { mode: 'VOICE' });
      }
      missionDraftIdRef.current = content.draft_id;
      setMissionDraftReady(true);
    }
  };

  const isVoiceChosenHandoff = isCheckInHandoff || !!commitmentId || !!missionEvolutionId;

  // Leaving the session returns to wherever the member came from (a dialogue
  // detail page, Explore, Journey, history…); /dashboard only when this screen
  // was the entry point (deep link / fresh tab, no history to go back to).
  const returnToOrigin = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/dashboard");
    }
  };

  // Element coaching / roleplay handoff (see Element Coaching & Roleplay contract).
  // Captured once on mount so the values stay stable as the user starts the session.
  const elementSession = (() => {
    const sp = new URLSearchParams(location.search);
    const action = sp.get('element_action');
    if (!action) return null;
    return {
      action, // "coaching" | "roleplay"
      topic: sp.get('topic') || null,
      domain: sp.get('domain') || null,
      level: sp.get('level') || null,
      situation: sp.get('situation') || null,
      reason: sp.get('reason') || null,
      relationship: sp.get('relationship') || null,
      scope: sp.get('scope') || null,
      // New contract fields:
      promptVariant: sp.get('prompt_variant') || null,
      conversationTopic: sp.get('conversation_topic') || null,
      question: sp.get('question') || null,
    };
  })();

  // Sessions launched from "Your Elements" (Talk it through with Aris / element
  // cards) are one-shot: hide the home-screen entry buttons and bounce back to
  // /dashboard when the user ends the session.
  const isElementsHandoff = !!elementSession;

  // Is this member mid-activation — handed off to the first-conversation
  // Activation but not yet finished?
  //
  // This is the voice counterpart of the module override in Services/Chat.js: a
  // pending member must run the ACTIVATION conversation rather than ordinary
  // coaching, because coaching can never complete activation, so the flag never
  // flips and the member is greeted as brand-new forever.
  //
  // Read once on mount so the value stays stable for the life of the screen —
  // activation completing mid-session must not change the dialog type of the
  // socket that is already open.
  const [isActivationSession] = useState(() =>
    isActivationPending(getCompletedStepsFromStorage())
  );

  // Activation finishing is recorded server-side and never appears in the
  // conversation, so the screen has to go looking for it. Same posture as the
  // chat interface: poll while pending, and check straight after each assistant
  // turn. Without this a member who activates by voice keeps the locked tab bar
  // until they navigate somewhere that re-checks the gate.
  const { onConversationEnd: onActivationConversationEnd } =
    useActivationCompletion();

  // Resume sessions launched from Session History are also a focused one-shot
  // entry: hide the home-screen entry CTAs ("Your Elements" / "Quick Session
  // Topics") and show a back-to-home button instead. Only individual resume
  // qualifies (couple/group/element/activation are not resumable).
  const isResumeHandoff =
    !!resumeConversationId && !isGroupCoaching && !isElementsHandoff && !isActivationSession;

  // Push element-session params onto a freshly-created RealtimeClient so the
  // first WebSocket handshake carries the element / roleplay query params.
  const applyElementSessionToClient = (client) => {
    if (!client || !elementSession) return;
    try {
      const ctx = {
        topic: elementSession.topic,
        domain: elementSession.domain,
        level: elementSession.level,
        situation: elementSession.situation,
        reason: elementSession.reason,
        relationship: elementSession.relationship,
        scope: elementSession.scope,
        prompt_variant: elementSession.promptVariant,
        conversation_topic: elementSession.conversationTopic,
        question: elementSession.question,
      };
      if (typeof client.setElementContext === 'function') {
        client.setElementContext(ctx);
      }
      // Per contract: send an explicit `dialog_type` for both flows. The
      // backend can infer them from the other params, but sending it
      // explicitly is the safest, self-documenting form.
      if (typeof client.setDialogType === 'function') {
        if (elementSession.action === 'roleplay') {
          client.setDialogType('roleplay');
        } else if (elementSession.action === 'coaching') {
          client.setDialogType('elements_coaching');
        }
      }
    } catch (e) {
      console.warn('[Coach] failed to apply element session context', e);
    }
  };

  // Push the resume_conversation_id onto a freshly-created RealtimeClient so the
  // WebSocket handshake opens the session in resume mode. Resume is individual
  // coaching only, so skip it for group/couple, element and activation flows
  // (the client also guards this, but bailing here keeps intent explicit).
  const applyResumeToClient = (client) => {
    if (!client || !resumeConversationId) return;
    if (isGroupCoaching || isElementsHandoff || isActivationSession) return;
    try {
      if (typeof client.setResumeConversationId === 'function') {
        client.setResumeConversationId(resumeConversationId);
      }
    } catch (e) {
      console.warn('[Coach] failed to apply resume conversation context', e);
    }
  };

  // Push dialog_type=activation onto a freshly-created RealtimeClient so the
  // WebSocket handshake opens the first-conversation Activation instead of open
  // coaching. Without it the backend infers INDIVIDUAL and builds the ordinary
  // coaching prompt, which is why activation only ever ran over chat.
  //
  // The backend gates on `dialog_type == DialogType.ACTIVATION`
  // (voice_instructions_manager.get_session_instructions), which swaps in the
  // activation system prompt and the Modules.ACTIVATION tools — the same prompt
  // and tools the text path uses, so the two entrypoints run one experience.
  //
  // Applied last so it wins over the element/roleplay dialog types above, and
  // deliberately NOT gated on topic_id: mirroring Services/Chat.js, a pending
  // member always gets ACTIVATION, never TOPIC_COACHING.
  const applyActivationToClient = (client) => {
    if (!client || !isActivationSession) return;
    // Couple/group activation does not exist on the backend, and the element,
    // roleplay and dialogue entrypoints are all withheld while pending
    // (ActivationGatedRoute). Reaching one of them means a stale deep link, so
    // honor the explicit intent and leave a trail rather than silently
    // overriding it.
    if (isGroupCoaching || isElementsHandoff || isDialogueHandoff || isVoiceChosenHandoff || resumeConversationId) {
      console.warn(
        '[Coach] member is mid-activation but this session was opened as a ' +
        'group/element/dialogue/resume handoff — leaving the dialog type alone'
      );
      return;
    }
    try {
      if (typeof client.setDialogType === 'function') {
        client.setDialogType('activation');
      }
    } catch (e) {
      console.warn('[Coach] failed to apply activation dialog type', e);
    }
  };
  const partnerIds = user_ids ? user_ids.split(',') : [];
  const relationIds = relation_ids ? relation_ids.split(',') : [];
  const [groupPartnerName, setGroupPartnerName] = useState("");
  const [groupFirstName, setGroupFirstName] = useState("");
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [recordingMode, setRecordingMode] = useState(false);
  const [handsFreeMode, setHandsFreeMode] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showActionPlanModal, setShowActionPlanModal] = useState(false);
  const [showSmartHabitModal, setShowSmartHabitModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [completeConversationHistory, setCompleteConversationHistory] =
    useState([]);
  const sessionIdRef = useRef(null);
  const voiceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const wavRecorderRef = useRef(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef(null);
  const clientRef = useRef(null);
  // Synchronous guard so two concurrent handleInstructions runs (mount + async
  // access resolution) can't both build a client. clientRef.current is only set
  // AFTER `await getUserFaith()`, so a ref-null check before the await races.
  const clientInitStartedRef = useRef(false);
  // Flips true once the realtime client is constructed. The listener-attaching effect
  // depends on this (a state value) instead of clientRef.current (a ref), so it reliably
  // re-runs and binds handlers to the client. A ref change does not trigger a render/effect.
  const [clientReady, setClientReady] = useState(false);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const inactivityTimeoutRef = useRef(null);
  const inactivityModalTimeoutRef = useRef(null);
  const [error, setError] = useState(null);
  const [disconnectText, setDisconnectText] = useState(null);
  const [showTimeoutText, setShowTimeoutText] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const prevChatRef = useRef(null);
  const [arisDisconnected, setArisDisconnected] = useState(false);
  const [tag, setTag] = useState(null);
  const [endSessionData, setEndSessionData] = useState(null);
  const [isLeavingPage, setIsLeavingPage] = useState(false);
  const pauseRef = useRef(false);
  const textRef = useRef(null);
  const [showConnectingText, setShowConnectingText] = useState(true);
  const secondsRef = useRef(null);
  const speechTimeRef = useRef(null);
  
  // Initialize secondsRef to a high value to prevent null issues during initial load
  if (secondsRef.current === null) {
    secondsRef.current = 999999;
  }
  
  // Ensure secondsRef is never null throughout the component lifecycle
  useEffect(() => {
    if (secondsRef.current === null) {
      secondsRef.current = 999999;
    }
  }, []);
  const userIDRef = useRef(null);
  const partnerIDsRef = useRef(partnerIds);
  const storeVoiceEmbeddingRef = useRef(false);
  const [interactionState, setInteractionState] = useState("idle");
  // Add these state variables at the top with your other state declarations
  const [pendingAudioTracks, setPendingAudioTracks] = useState(false);
  const pendingAudioTracksRef = useRef(false);
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);
  const isBotSpeakingRef = useRef(false);
  // Transient retry notice shown in place of connecting text
  const [retryNoticeText, setRetryNoticeText] = useState(null);
  const retryNoticeTimerRef = useRef(null);
  // Show a thinking/processing status between user turn end and assistant reply
  const [showThinking, setShowThinking] = useState(false);
  // Live session time scheduling modal control
  const [showLiveSessionModal, setShowLiveSessionModal] = useState(false);
  const lowTimeDebounceRef = useRef(0);
  const [showLowTimeBanner, setShowLowTimeBanner] = useState(false);
  const [timeTick, setTimeTick] = useState(0);
  // While Aris is speaking, we count down provisionally until server low-warning sync arrives
  const awaitingServerSyncRef = useRef(false);
  // If time ran out while Aris is speaking, we defer disconnect until speech completes
  const pendingTimeoutCloseRef = useRef(false);
  // Session state management
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [showVoiceHistory, setShowVoiceHistory] = useState(false);
  const [showMinutesModal, setShowMinutesModal] = useState(false);
  const [showKeyboardSwitchModal, setShowKeyboardSwitchModal] = useState(false);
  const [showLeaveSessionModal, setShowLeaveSessionModal] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState("Welcome! Click the mic to start your first coaching session");
  // Coaching Topics state
  const [showCoachingTopicsModal, setShowCoachingTopicsModal] = useState(false);
  const [showTopicOptionsModal, setShowTopicOptionsModal] = useState(false);
  const [showYourElementsModal, setShowYourElementsModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [activeTopicSession, setActiveTopicSession] = useState(null);
  const [topicsSlideFromLeft, setTopicsSlideFromLeft] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Client-side VAD state
  const vadRef = useRef(null);
  const clientVadModeRef = useRef(false);
  const vadSpeakingRef = useRef(false);
  // Track if we've appended any user audio since the last response (client VAD mode)
  const appendedSinceLastTurnRef = useRef(false);
  const preRollRef = useRef([]); // array of Int16Array 16k frames
  const preRollSamplesRef = useRef(0);
  const MAX_PREROLL_SAMPLES = 0.3 * 16000; // 300ms
  const micVadRef = useRef(null);
  const attachPrerollNextFrameRef = useRef(false);
  const speechEndTimerRef = useRef(null);
  // NOTE: previously used to downsample mic frames to 16k for backend wake-word
  // detection while Aris was speaking. That streaming has been removed, so this
  // helper is currently unused — kept here (commented) in case it's needed again.
  // const resamplePCM16 = (input, fromRate, toRate) => {
  //   const src = input instanceof Int16Array ? input : new Int16Array(input);
  //   if (fromRate === toRate) return src;
  //   const ratio = toRate / fromRate;
  //   const newLen = Math.max(1, Math.floor(src.length * ratio));
  //   const out = new Int16Array(newLen);
  //   for (let i = 0; i < newLen; i++) {
  //     const pos = i / ratio;
  //     const idx = Math.floor(pos);
  //     const frac = pos - idx;
  //     const s0 = src[idx] || 0;
  //     const s1 = src[idx + 1] || s0;
  //     out[i] = (s0 + (s1 - s0) * frac) | 0;
  //   }
  //   return out;
  // };
  // Track if the user has spoken at least once in this session (to gate inactivity modal)
  const hasUserSpokenRef = useRef(false);
  const wakeLockRef = useRef(null);

  const requestWakeLock = useCallback(async () => {
    try {
      if (!isMobile) return;
      if ("wakeLock" in navigator && !wakeLockRef.current) {
        const lock = await navigator.wakeLock.request("screen");
        wakeLockRef.current = lock;
        lock.addEventListener("release", () => {
          wakeLockRef.current = null;
        });
      }
    } catch (e) {
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (e) {
    }
  }, []);

  // Stop local mic capture and VAD
  const stopClientAudio = useCallback(async () => {
    // First, stop MicVAD as it has its own mic stream
    // MicVAD from @ricky0123/vad-web exposes audioContext and stream that need manual cleanup
    try {
      const vad = micVadRef.current;
      // Claim the ref synchronously so a concurrent teardown path (e.g. the
      // socket `close` handler firing alongside this) can't also call destroy()
      // and double-close the AudioContext.
      micVadRef.current = null;
      if (vad) {
        // Pause VAD processing. In @ricky0123/vad-web, pause() is what actually
        // stops the mic stream tracks (via the default pauseStream); await it so
        // the tracks are released before we move on.
        try { await vad.pause?.(); } catch { }

        // Defensive: stop the MicVAD's media stream tracks directly. NOTE the
        // property is the private `_stream` in vad-web@0.0.29 (NOT `stream`), so
        // the previous `vad.stream` access was a silent no-op and the mic could
        // stay lit on disconnect. This also covers the case where pause() above
        // early-returned because the VAD was already marked not-listening.
        try {
          const vadStream = vad._stream || vad.stream;
          if (vadStream && typeof vadStream.getTracks === 'function') {
            vadStream.getTracks().forEach((track) => {
              try { track.stop(); } catch { }
            });
          }
        } catch { }

        // Let destroy() close its own AudioContext. Do NOT close it ourselves:
        // destroy() calls `_audioContext.close()` WITHOUT awaiting, so closing it
        // here first makes that internal close reject as an uncaught
        // "Cannot close a closed AudioContext" InvalidStateError.
        try { vad.destroy?.(); } catch { }
      }
    } catch { }

    // Then stop WavRecorder
    try {
      const rec = wavRecorderRef.current;
      if (rec) {
        // Pause if recording
        try {
          if (typeof rec.getStatus === "function" && rec.getStatus() === "recording") {
            await rec.pause();
          }
        } catch { }

        // Use destroy() for forceful cleanup - this ensures mic is released
        // even if recorder is in an inconsistent state
        try {
          await rec.destroy();
        } catch { }
      }
    } catch { }
  }, []);

  useEffect(() => {
    // Use 48kHz for native Opus playback without resampling
    // This eliminates resampling artifacts and noise
    wavStreamPlayerRef.current = new WavStreamPlayer48k({
      sampleRate: 48000,
      bufferMs: 400, // Initial buffer (will be adjusted based on network)
      adaptiveBuffering: true // Enable network-adaptive buffering
    });
    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("keydown", handleUserActivity);
    window.addEventListener("scroll", handleUserActivity);
    window.addEventListener("click", handleUserActivity);

    resetInactivityTimer();

    return () => {
      // Ensure mic is fully stopped on unmount/navigation
      stopClientAudio();
      clearInactivityTimer();
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keydown", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
    };
  }, []);

  // keep ref in sync whenever state changes
  useEffect(() => {
    pendingAudioTracksRef.current = pendingAudioTracks;
  }, [pendingAudioTracks]);

  // keep isBotSpeaking ref in sync to avoid stale closures in long-lived callbacks
  useEffect(() => {
    isBotSpeakingRef.current = isBotSpeaking;
  }, [isBotSpeaking]);

  // Fetch welcome message on component mount
  useEffect(() => {
    const fetchWelcomeMsg = async () => {
      try {
        const message = await getWelcomeMessage();
        setWelcomeMessage(message);
      } catch (error) {
        console.error('Error fetching welcome message:', error);
        setWelcomeMessage("Welcome! Click the mic to start your first coaching session");
      }
    };

    fetchWelcomeMsg();
  }, []);

  useEffect(() => {
    const handleInstructions = async () => {
      setHeaderContent(true);
      try {
        // For web users, use functionality access system instead of deprecated API
        // Group coaching still needs the API for partner info
        let response;
        if (isGroupCoaching) {
          response = await fetchGroupInstructions(relationIds[0]);
        }

        // Get voice preference from profile
        const voice = profileContent?.aris_voice || 'alloy';
        voiceRef.current = getOpenAIVoice(voice);
        
        // Use functionality access for coaching minutes
        // IMPORTANT: Check for actual access data, not just initialized flag
        // This prevents race condition where initialized=true but access data is still null/stale
        if (accessInitialized && access) {
          const isUnlimited = hasUnlimitedCoaching();
          const coachingMinutes = getCoachingMinutes();
          if (isUnlimited || coachingMinutes.unlimited) {
            // User has unlimited coaching - set to high value
            secondsRef.current = 999999;
          } else {
            // User has limited coaching - convert minutes to seconds
            secondsRef.current = coachingMinutes.available * 60;
          }
        } else {
          // Default to unlimited while loading or if no access data
          secondsRef.current = 999999;
        }
        
        userIDRef.current = profileContent?._id;
        
        if (isGroupCoaching && response?.payload) {
          setGroupPartnerName(response.payload.partner_name || "");
          setGroupFirstName(response.payload.first_name || "");
        }

          storeVoiceEmbeddingRef.current = true;

          // Build the realtime client only ONCE. This effect re-runs whenever
          // useFunctionalityAccess resolves (access / accessInitialized change), but the
          // client config does not depend on access data. Rebuilding created a second
          // client that the listener effect never bound to — so audio arrived on a client
          // with no conversation.updated handler and never played. Guard against that.
          // NOTE: the flag is set SYNCHRONOUSLY (before the await below) so two
          // concurrent runs can't both pass the check and each build a client.
          if (!clientInitStartedRef.current) {
          clientInitStartedRef.current = true;
          try {
            const faithResponse = await getUserFaith();

            let opusDecoder = null;
            try {
              // Use 48kHz for native Opus playback without resampling
              opusDecoder = new BrowserOpusDecoder(48000, 1, false);
            } catch (e) {
              // If Opus decoder/WASM fails to initialize, fall back to PCM16-only.
              // Backend should continue to send codec: "pcm16" in this case.
              console.error("Opus decoder construction failed; falling back to PCM16", e);
              opusDecoder = null;
            }

            const clientConfig = {
              user: userIDRef.current,
              debug: false,
              voice: voiceRef.current,
              // Backend treats belief_system as a plain string inserted
              // literally into the prompt (faith_enabled = bool(belief_system)).
              // Send the bare faith name when enabled, empty string otherwise —
              // never a JSON blob or a truthy '{}' sentinel.
              belief_system: faithResponse && faithResponse.payload.toggle_state
                ? faithResponse.payload.faith
                : '',
              opusDecoder,
            };

            // Add user_ids parameter for group coaching
            if (isGroupCoaching && partnerIDsRef.current && partnerIDsRef.current.length > 0) {
              clientConfig.user_ids = JSON.stringify(partnerIDsRef.current);
            }

            clientRef.current = new RealtimeClient(clientConfig);
            applyElementSessionToClient(clientRef.current);
            applyResumeToClient(clientRef.current);
            applyActivationToClient(clientRef.current);
            setClientReady(true);
          } catch (error) {
            console.error("Error fetching user faith:", error);

            let opusDecoder = null;
            try {
              // Use 48kHz for native Opus playback without resampling
              opusDecoder = new BrowserOpusDecoder(48000, 1, false);
            } catch (e) {
              console.error("Opus decoder construction failed; falling back to PCM16", e);
              opusDecoder = null;
            }

            const clientConfig = {
              user: userIDRef.current,
              debug: false,
              voice: voiceRef.current,
              belief_system: '',
              opusDecoder,
            };

            // Add partner_id parameter for group coaching
            if (isGroupCoaching && partnerIDsRef.current && partnerIDsRef.current.length > 0) {
              clientConfig.user_ids = JSON.stringify(partnerIDsRef.current);
            }

            clientRef.current = new RealtimeClient(clientConfig);
            applyElementSessionToClient(clientRef.current);
            applyResumeToClient(clientRef.current);
            applyActivationToClient(clientRef.current);
            setClientReady(true);
          }
          } // end if (!clientInitStartedRef.current) — build client only once
          // Don't auto-start connection - wait for user to click mic
          setLoading(false);
      } catch (error) {
        setError("Server is not responding. Please try again later.");
        console.error("Error fetching instructions:", error.message);
      } finally {
        setLoading(false);
      }
    };

    handleInstructions();
  }, [isGroupCoaching, accessInitialized, hasUnlimitedCoaching, access]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Only show timeout if user doesn't have unlimited coaching
    if (handsFreeMode && accessInitialized && typeof secondsRef.current === 'number' && secondsRef.current <= 0 && !hasUnlimitedCoaching()) {
      setHandsFreeMode(false);
      setShowTimeoutText(
        "Your time is up. Please add more time to continue the session."
      );
    } else if (handsFreeMode) {
      changeTurnEndType("client_vad");
    } else {
      changeTurnEndType("none");
    }
  }, [handsFreeMode]);

  useEffect(() => {
    handleUserActivity();
  }, [sessionIdRef.current]);

  useEffect(() => {
    const unloadHandler = () => {
      // Only report end_session when an active session exists and wasn't already disconnected
      if (!isSessionActive) return;
      if (!sessionIdRef.current) return;
      if (arisDisconnected) return;

      try {
        const token = Cookies.get("idtoken");
        const chatbaseUrl = process.env.REACT_APP_BASE_URL_CHAT_ARIS;
        const url = new URL("/api/coaching/end_session", chatbaseUrl);
        url.searchParams.append("session_id", sessionIdRef.current);
        url.searchParams.append(
          "time",
          parseFloat(secondsRef.current / 60).toFixed(2)
        );
        url.searchParams.append("status", false);

        fetch(url.toString(), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          keepalive: true,
        });
      } catch (_) {
        // best-effort on unload
      }
    };
    window.addEventListener("unload", unloadHandler);
    return () => {
      window.removeEventListener("unload", unloadHandler);
    };
  }, [isSessionActive, arisDisconnected, isLeavingPage]);

  useEffect(() => {
    // Only show timeout if user doesn't have unlimited coaching
    // Guard: secondsRef.current starts as null; null <= 0 is true in JS, so require a finite number AND initialized access
    if (accessInitialized && typeof secondsRef.current === 'number' && secondsRef.current <= 0 && !hasUnlimitedCoaching()) {
      setShowTimeoutText(
        "Your time is up. Please add more time to continue the session."
      );
      // If Aris is speaking, defer disconnect until speech completes
      if (interactionState === "arisSpeaking") {
        pendingTimeoutCloseRef.current = true;
      } else {
        if (clientRef.current && clientRef.current.isConnected()) {
          clientRef.current.disconnect();
        }

        // Release ALL local capture (WavRecorder AND MicVAD). Calling only
        // wavRecorder.destroy() left MicVAD's own mic stream open in handsfree
        // mode, so the browser mic indicator stayed lit after the session ended.
        stopClientAudio();
      }
    }
  }, [secondsRef.current, interactionState]);

  // Countdown: user speaking/recording always decrements. During Aris speaking, decrement provisionally
  // only while we are awaiting the server's minutes_low_warning sync for that turn.
  useEffect(() => {
    let intervalId;
    const seconds = Number(secondsRef.current);
    const isConsuming =
      interactionState === "userSpeaking" ||
      recordingMode ||
      (interactionState === "arisSpeaking" && awaitingServerSyncRef.current === true);

    if (isConsuming && Number.isFinite(seconds) && seconds > 0) {
      intervalId = setInterval(() => {
        // Guard against null/undefined secondsRef during initialization
        if (!accessInitialized || secondsRef.current === null) return;
        // Don't decrement for unlimited coaching users
        if (hasUnlimitedCoaching()) return;
        const current = Number(secondsRef.current) || 0;
        if (current <= 0) return;
        const next = Math.max(0, current - 1);
        if (next === 0 && interactionState === "arisSpeaking") {
          // Defer disconnect until speech completes
          pendingTimeoutCloseRef.current = true;
        }
        secondsRef.current = next;
        setTimeTick((t) => t + 1);
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [interactionState, recordingMode, timeTick]);

  // Track Aris speaking to manage provisional countdown and deferred disconnect
  useEffect(() => {
    if (interactionState === "arisSpeaking") {
      // Start provisional countdown until server sync arrives
      awaitingServerSyncRef.current = true;
    } else {
      // Leaving arisSpeaking: stop provisional countdown
      awaitingServerSyncRef.current = false;
      // If we were deferring a close due to timeout, close now
      if (pendingTimeoutCloseRef.current && accessInitialized && typeof secondsRef.current === 'number' && Number(secondsRef.current) <= 0 && !hasUnlimitedCoaching()) {
        pendingTimeoutCloseRef.current = false;
        setShowConnectingText(false);
        setShowTimeoutText(
          "Your time is up. Please add more time to continue the session."
        );
        try {
          const c = clientRef.current;
          if (c && c.isConnected && c.isConnected()) c.disconnect();
        } catch { }
        // Release mic + VAD on this deferred timeout close too.
        try { stopClientAudio(); } catch { }
        setArisDisconnected(true);
      }
    }
  }, [interactionState]);

  useEffect(() => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;
    if (!client || !wavStreamPlayer) return;

    // When new audio is added, track it
    wavStreamPlayer.on("audio.data", ({ trackId, buffer }) => {
      setIsBotSpeaking(true);
      // Assistant audio has begun, stop showing thinking
      setShowThinking(false);

      pendingAudioTracksRef.current = true;
      // Add this track to pending tracks
      setPendingAudioTracks(true);
      // Treat incoming assistant audio as activity to prevent inactivity timeout
      try { handleUserActivity(); } catch { }
    });

    // Adaptively increase playback buffer on underruns to smooth playback
    wavStreamPlayer.on("buffer.underrun", ({ trackId, graceFrames }) => {
      try {
        const current = typeof wavStreamPlayer.bufferMs === "number" ? wavStreamPlayer.bufferMs : 400;
        const next = Math.min(2000, current + 400);
        if (next !== current) {
          wavStreamPlayer.setBufferMs(next);
        }
      } catch (_e) {
      }
    });

    // When a track completes, remove it from pending
    wavStreamPlayer.on("track.complete", (trackId) => {
      pendingAudioTracksRef.current = false;
      setPendingAudioTracks(false);

      if (interactionState === "arisSpeaking") {
        // After assistant finishes, return to idle; VAD/press-and-hold will update to
        // 'userSpeaking' when the user actually speaks.
        setInteractionState("idle");
      }
    });

    client.realtime.on('conversation.item.remove', (event) => {
      pendingAudioTracksRef.current = false;
      setPendingAudioTracks(false);

    });

    // Backend wake-word detection: stop playback and enter listening mode
    const handleWakeDetected = async (evt) => {
      try {
        const trackSampleOffset = await wavStreamPlayer.interrupt();
        // Optionally cancel server generation similar to interruption flow
        try {
          if (trackSampleOffset?.trackId) {
            const { trackId, offset } = trackSampleOffset;
            await client.cancelResponse(trackId, offset);
          }
        } catch { }
      } catch { }
      setIsBotSpeaking(false);
      setInteractionState('userSpeaking');
      hasUserSpokenRef.current = true;
      try { handleUserActivity(); } catch { }
    };
    client.realtime.on('server.wake.detected', handleWakeDetected);

    return () => {
      // Clean up event listener
      if (wavStreamPlayer) {
        wavStreamPlayer.off("track.complete");
        wavStreamPlayer.off("audio.data");
        wavStreamPlayer.off("buffer.underrun");
      }
      if (client) {
        client.realtime.off('conversation.item.remove');
        client.realtime.off('server.wake.detected');
      }
    };
  }, [interactionState, handsFreeMode]);

  useEffect(() => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;
    if (!client) {
      return;
    }

    client.on("error", (event) => {
      console.error("Client error:", event);
      setError("Connection to Aris was interrupted. Please try again later.");
    });

    // Show retry notice from server and auto-clear after 4s
    const handleRetryNotice = (evt) => {
      try {
        const content = evt?.content || "Still working on it — a quick hiccup on our end. Retrying now.";
        setRetryNoticeText(content);
        // Ensure connecting text is not shown simultaneously
        setShowConnectingText(false);
      } catch { }
    };
    client.on("session.response_retry_notice", handleRetryNotice);

    client.on("conversation.interrupted", async () => {
      textRef.current = "";
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
        pauseRef.current = true;
      }
    });

    // Add immediate interruption on server VAD speech detection
    client.realtime.on("conversation.speaker_started", async () => {
      // Immediately interrupt audio playback when user starts speaking
      if (isBotSpeaking && handsFreeMode) {
        await wavStreamPlayer.interrupt();
        setIsBotSpeaking(false);
      }
      // Regardless of interruption, reflect that the user started speaking
      setInteractionState("userSpeaking");
      // Mark that the user has spoken at least once; used to gate inactivity modal
      hasUserSpokenRef.current = true;
      // Any detected speech from user counts as activity
      try { handleUserActivity(); } catch { }
    });

    client.on("session.started", async ({ session_id }) => {
      sessionIdRef.current = session_id;
    })

    client.on("conversation.updated", async ({ item, delta }) => {
      if (item.role === "assistant" && delta?.audio) {
        setShowConnectingText(false);
        setRetryNoticeText(null); // clear retry notice on response arrival
        setShowThinking(false);
        setInteractionState("arisSpeaking");
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
        textRef.current = item.formatted.transcript;
      } else if (delta?.text) {
        setShowConnectingText(false);
        setRetryNoticeText(null); // clear retry notice on response arrival
        setShowThinking(false);
        textRef.current = item.formatted.transcript;
      }

      // Don't mark track complete here - wait for binary_audio_complete event
      // This ensures all binary chunks have arrived before marking complete

      if (item.status === "completed" && item.formatted.audio?.length) {
        const history = client.conversation
          .getItems()
          .filter(
            (item) =>
              item.type === "message" &&
              item.status === "completed" &&
              (item.role !== "user" ||
                item.formatted.transcript ||
                item.formatted.text)
          )
          .map((item) => ({
            role: item.role,
            content: item.formatted.transcript || item.formatted.text,
          }));

        if (history.length > 0) {
          setCompleteConversationHistory(history);
        }
      }
    });

    // Listen for low time warning
    const handleLowTimeWarning = (event) => {
      try {
        const remaining = Number(
          event?.content?.remaining_seconds ?? event?.remaining_seconds ?? NaN
        );
        // If time is up (or invalid), force UI to show add-minutes message
        // Only show if user doesn't have unlimited coaching
        if (accessInitialized && (!Number.isFinite(remaining) || remaining <= 0) && !hasUnlimitedCoaching()) {
          secondsRef.current = 0;
          setShowConnectingText(false);
          setShowTimeoutText(
            "Your time is up. Please add more time to continue the session."
          );
          // If Aris is speaking, defer disconnect until speech completes
          if (interactionState === "arisSpeaking") {
            pendingTimeoutCloseRef.current = true;
          } else {
            // Fully release mic + VAD (pause() alone does NOT stop the mic
            // tracks), then disconnect if connected.
            try { stopClientAudio(); } catch { }
            try {
              const c = clientRef.current;
              if (c && c.isConnected && c.isConnected()) c.disconnect();
            } catch { }
            setArisDisconnected(true);
          }
          return;
        }

        // Non-zero remaining time: take server as source of truth and sync local counter
        secondsRef.current = Math.max(0, remaining);
        setTimeTick((t) => t + 1);
        // We received authoritative server value for this turn; stop provisional decrement
        awaitingServerSyncRef.current = false;
        // Optional: simple debounce if server spams frequent warnings
        const now = Date.now();
        if (now - lowTimeDebounceRef.current < 1000) return; // basic 1s debounce
        lowTimeDebounceRef.current = now;
      } catch (e) {
        // Fallback to showing timeout message on any parsing error
        // Only show if user doesn't have unlimited coaching
        if (accessInitialized && !hasUnlimitedCoaching()) {
          secondsRef.current = 0;
          setShowConnectingText(false);
          setShowTimeoutText(
            "Your time is up. Please add more time to continue the session."
          );
        }
        if (interactionState === "arisSpeaking") {
          pendingTimeoutCloseRef.current = true;
        } else {
          try { stopClientAudio(); } catch { }
          try {
            const c = clientRef.current;
            if (c && c.isConnected && c.isConnected()) c.disconnect();
          } catch { }
          setArisDisconnected(true);
        }
      }
    };

    client.on("session.minutes_low_warning", handleLowTimeWarning);

    // Listen for binary audio completion (after all chunks received)
    client.on("conversation.binary_audio_complete", ({ item_id }) => {
      wavStreamPlayer.markTrackComplete(item_id);
    });

    return () => {
      // Always remove listeners before resetting the client; reset() clears handlers
      client.off("session.response_retry_notice", handleRetryNotice);
      client.off('session.minutes_low_warning', handleLowTimeWarning);
      if (retryNoticeTimerRef.current) {
        clearTimeout(retryNoticeTimerRef.current);
        retryNoticeTimerRef.current = null;
      }
      client.reset();
    };
  }, [clientReady]);

  const resetInactivityTimer = () => {
    // Only run inactivity logic during an active session AND after the user has spoken at least once
    if (arisDisconnected || !isSessionActive || !hasUserSpokenRef.current) {
      return;
    }

    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    if (inactivityModalTimeoutRef.current) {
      clearTimeout(inactivityModalTimeoutRef.current);
    }

    inactivityTimeoutRef.current = setTimeout(() => {
      if (isSessionActive && hasUserSpokenRef.current) {
        setShowInactivityModal(true);
        inactivityModalTimeoutRef.current = setTimeout(() => {
          setShowInactivityModal(false);
          disconnectConversation(true, false, "dashboard");
        }, 3 * 60 * 1000);
      }
    }, 5 * 60 * 1000);
  };

  const clearInactivityTimer = () => {
    try {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      if (inactivityModalTimeoutRef.current) {
        clearTimeout(inactivityModalTimeoutRef.current);
        inactivityModalTimeoutRef.current = null;
      }
      setShowInactivityModal(false);
    } catch { }
  };

  const handleUserActivity = () => {
    if (!arisDisconnected && isSessionActive && hasUserSpokenRef.current) {
      resetInactivityTimer();
    }
  };

  // If session ends or hasn't started, ensure inactivity timers are cleared and modal hidden
  useEffect(() => {
    if (!isSessionActive) {
      clearInactivityTimer();
    }
  }, [isSessionActive]);

  useEffect(() => {
    if (!isMobile) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isSessionActive) {
        requestWakeLock();
      } else {
        releaseWakeLock();
      }
    };

    if (isSessionActive) {
      requestWakeLock();
      document.addEventListener("visibilitychange", handleVisibilityChange);
    } else {
      releaseWakeLock();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      releaseWakeLock();
    };
  }, [isSessionActive, requestWakeLock, releaseWakeLock]);

  // Removed beforeUnloadHandler - now using modal for active sessions

  useEffect(() => {
    return () => {
      // Stop MicVAD first (has its own mic stream)
      // MicVAD exposes audioContext and stream that need manual cleanup
      try {
        const vad = micVadRef.current;
        micVadRef.current = null;
        if (vad) {
          try { vad.pause?.(); } catch { }
          // Manually stop MicVAD's media stream tracks. The real property is the
          // private `_stream` in vad-web@0.0.29 (`vad.stream` is undefined).
          try {
            const vadStream = vad._stream || vad.stream;
            if (vadStream && typeof vadStream.getTracks === 'function') {
              vadStream.getTracks().forEach((track) => {
                try { track.stop(); } catch { }
              });
            }
          } catch { }
          // destroy() closes its own (owned) AudioContext; closing it ourselves
          // first would make destroy()'s un-awaited close reject uncaught.
          try { vad.destroy?.(); } catch { }
        }
      } catch { }

      // Stop mic capture using destroy() for forceful cleanup
      try {
        const rec = wavRecorderRef.current;
        if (rec) {
          if (rec.getStatus && rec.getStatus() === 'recording') {
            rec.pause().catch(() => { });
          }
          // Use destroy() to ensure mic is released
          rec.destroy && rec.destroy().catch(() => { });
        }
      } catch (e) { }

      // Stop playback
      try {
        const player = wavStreamPlayerRef.current;
        if (player && player.interrupt) {
          player.interrupt();
        }
      } catch (e) { }

      // Disconnect socket
      try {
        const client = clientRef.current;
        if (client && client.isConnected && client.isConnected()) {
          client.disconnect();
        }
      } catch (e) { }
    };
  }, []);

  useEffect(() => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    wavStreamPlayer.on('all.audio.complete', () => {
      setInteractionState('idle');
    });

    return () => {
      wavStreamPlayer.off('all.audio.complete');
    };
  }, [wavStreamPlayerRef]);

  // Utility: upsample Int16 mono from 16 kHz to 24 kHz using linear interpolation
  // const upsample16kTo24k = useCallback((input) => {
  //   try {
  //     const src = input instanceof Int16Array ? input : new Int16Array(input);
  //     const inLen = src.length;
  //     if (inLen === 0) return new Int16Array(0);
  //     const ratio = 1.5; // 16k -> 24k
  //     const outLen = Math.floor(inLen * ratio);
  //     const out = new Int16Array(outLen);
  //     for (let i = 0; i < outLen; i++) {
  //       const t = i / ratio;
  //       const i0 = Math.floor(t);
  //       const i1 = Math.min(i0 + 1, inLen - 1);
  //       const frac = t - i0;
  //       const v = src[i0] * (1 - frac) + src[i1] * frac;
  //       // clamp to int16
  //       out[i] = v < -32768 ? -32768 : v > 32767 ? 32767 : v;
  //     }
  //     return out;
  //   } catch (e) {
  //     // On any failure, fall back to original buffer to avoid breaking audio
  //     return input instanceof Int16Array ? input : new Int16Array(input);
  //   }
  // }, []);

  const connectConversation = useCallback(async () => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    // The realtime client is built asynchronously (handleInstructions awaits
    // getUserFaith), so a caller that fires too early would otherwise open the
    // mic and then blow up on `client.realtime`. Bail before touching audio.
    if (!client || !wavStreamPlayer) {
      setError('Error connecting to Aris. Please try again later.');
      return;
    }

    // The session's context (Dialogue, check-in, commitment, Mission Evolution) goes on
    // the client before either transport connects.
    const applySessionContexts = async () => {
      // Dialogue session: wait for the prepared instance (usually already
      // resolved by the time the member taps the mic) and attach it so the
      // handshake runs the dialogue program.
      if (dialogueInstancePromiseRef.current) {
        const instance = await dialogueInstancePromiseRef.current;
        if (instance?.instance_id && typeof client.setDialogueContext === 'function') {
          client.setDialogueContext(instance.instance_id);
        }
      }

      // Always set (null clears it) so a reused client never carries a
      // finished check-in into an ordinary session.
      if (typeof client.setCheckinContext === 'function') {
        client.setCheckinContext(checkinSessionId || null);
      }
      if (typeof client.setCommitmentContext === 'function') {
        client.setCommitmentContext(commitmentId || null);
      }
      if (typeof client.setMissionEvolutionContext === 'function') {
        client.setMissionEvolutionContext(missionEvolutionId || null);
      }
    };

    // Connect the audio player FIRST so AudioContext.resume() runs in the
    // synchronous user-gesture call stack. If wavRecorder.begin() (getUserMedia)
    // runs first, the async hop can cause Safari to ignore the resume().
    await wavStreamPlayer.connect();
    await wavRecorder.begin();

    try {
      let isSocketClosed = false;

      const handleTrackComplete = (trackId) => {
        setIsBotSpeaking(false);
      };

      wavStreamPlayer.on('track.activation.complete', handleTrackComplete);

      const handleClose = async () => {
        isSocketClosed = true;
        // Fully release local capture on socket close. pause() alone leaves the
        // MediaStream tracks (and any MicVAD mic stream) live, so the browser mic
        // indicator stayed on when the server ended the session. stopClientAudio()
        // stops WavRecorder AND MicVAD tracks.
        try {
          await stopClientAudio();
        } catch (e) {
          console.warn('Error releasing mic on socket close:', e);
        }
        // If user is out of time, ensure we show the add-minutes message instead of "Connecting to Aris..."
        // Only show if user doesn't have unlimited coaching
        if (accessInitialized && typeof secondsRef.current === 'number' && Number(secondsRef.current) <= 0 && !hasUnlimitedCoaching()) {
          setShowConnectingText(false);
          setShowTimeoutText(
            "Your time is up. Please add more time to continue the session."
          );
          setArisDisconnected(true);
        }
      };

      client.realtime.on('close_connection', () => {
        isSocketClosed = true;
      });

      client.realtime.on('close', handleClose);

      await applySessionContexts();
      if (missionEvolutionId) {
        client.realtime.off('server.mission_evolution');
        client.realtime.on('server.mission_evolution', handleMissionEvolutionFrame);
      }

      const response = await client.connect();

      if (response) {
        setLoading(false);
        if (client.getTurnDetectionType() === "server_vad") {
          await wavRecorder.record((data) => {
            // Guard against sending after disconnect/close
            if (!client.isConnected()) return;
            try {
              // Any mic activity resets inactivity timer
              try { handleUserActivity(); } catch { }
              // Recorder is 16 kHz; backend expects 24 kHz PCM16. Upsample 16k -> 24k.
              // const upsampled = upsample16kTo24k(data.mono);
              // Do not stream mic audio while Aris is speaking or still has audio queued.
              if (!isBotSpeakingRef.current && !pendingAudioTracksRef.current) {
                client.appendInputAudio(data.mono);
              }
            } catch (e) {
              // Ignore send errors if socket is closed
            }
          });
        }
      }

      return () => {
        client.realtime.off('close', handleClose);
        client.realtime.off('close_connection');
        wavStreamPlayer.off('track.activation.complete', handleTrackComplete);
      };
    } catch (error) {
      setError('Error connecting to Aris. Please try again later.');
    }
  }, [navigate, arisDisconnected]);

  const changeTurnEndType = async (value) => {
    const client = clientRef.current;
    if (!client) return;

    const wavRecorder = wavRecorderRef.current;
    // If switching away from server VAD, finalize the current user turn by committing
    // buffered audio and triggering response.create BEFORE updating the session.
    try {
      const prevType = client.getTurnDetectionType && client.getTurnDetectionType();
      if (
        prevType === "server_vad" &&
        (value === "client_vad" || value === "none") &&
        client.isConnected && client.isConnected()
      ) {
        try { client.realtime.send("input_audio_buffer.commit"); } catch (e) { console.warn("Failed to commit input audio buffer before mode switch:", e); }
        try { client.realtime.send("response.create"); } catch (e) { console.warn("Failed to send response.create before mode switch:", e); }
      }
    } catch (e) {
      console.warn("Error preparing response before VAD mode switch:", e);
    }

    // If switching away from client_vad while user is speaking OR we have buffered frames,
    // finalize turn explicitly because MicVAD teardown may prevent onSpeechEnd from firing.
    try {
      const prevType = client.getTurnDetectionType && client.getTurnDetectionType();
      if (
        prevType === null &&
        (value === "none" || value === "server_vad") &&
        client.isConnected && client.isConnected() &&
        (vadSpeakingRef.current === true || appendedSinceLastTurnRef.current === true)
      ) {
        try { await client.createResponse(); } catch (e) { console.warn("Failed to createResponse on client_vad ->", value, e); }
        appendedSinceLastTurnRef.current = false;
      }
    } catch (e) {
      console.warn("Error finalizing client_vad turn during mode switch:", e);
    }

    if (value === "none" && wavRecorder.getStatus() === "recording") {
      await wavRecorder.pause();
    }

    if (value === "server_vad") {
      clientVadModeRef.current = false;
      client.updateSession({ turn_detection: { type: "server_vad" } });
    } else if (value === "client_vad") {
      clientVadModeRef.current = true;
      client.updateSession({ turn_detection: null });
    } else {
      clientVadModeRef.current = false;
      client.updateSession({ turn_detection: null });
    }

    // Stop any existing recorder loop and restart based on mode
    if (wavRecorder.getStatus() === "recording") {
      await wavRecorder.pause();
    }

    // Reset VAD state
    vadRef.current = null;
    vadSpeakingRef.current = false;
    preRollRef.current = [];
    preRollSamplesRef.current = 0;
    // Stop any existing MicVAD instance with proper cleanup
    try {
      if (micVadRef.current) {
        const vad = micVadRef.current;
        vad.pause && vad.pause();
        // Manually stop MicVAD's media stream tracks to release mic. The real
        // property is the private `_stream` in vad-web@0.0.29.
        try {
          const vadStream = vad._stream || vad.stream;
          if (vadStream && typeof vadStream.getTracks === 'function') {
            vadStream.getTracks().forEach((track) => {
              try { track.stop(); } catch { }
            });
          }
        } catch { }
        // destroy() closes its own (owned) AudioContext; don't close it here too
        // or destroy()'s un-awaited internal close rejects uncaught.
        vad.destroy && vad.destroy();
      }
    } catch { }
    micVadRef.current = null;

    if (value === "server_vad" && client.isConnected()) {
      // Ensure wavRecorder session is started before recording
      const recorderStatus = wavRecorder.getStatus();
      if (recorderStatus === "ended" || recorderStatus === "paused") {
        try {
          await wavRecorder.begin();
        } catch (e) {
          console.warn("Failed to begin wavRecorder for server_vad:", e);
        }
      }
      await wavRecorder.record((data) => {
        if (!client.isConnected()) return;
        try {
          // Any mic activity resets inactivity timer
          try { handleUserActivity(); } catch { }
          // const upsampled = upsample16kTo24k(data.mono);
          // Do not stream mic audio while Aris is speaking or still has audio queued.
          if (!isBotSpeakingRef.current && !pendingAudioTracksRef.current) {
            client.appendInputAudio(data.mono);
          }
        } catch (e) {
          // swallow errors on disconnect
        }
      });
    } else if (value === "client_vad" && client.isConnected()) {
      // Ensure wavRecorder session is started before recording
      const recorderStatus = wavRecorder.getStatus();
      if (recorderStatus === "ended" || recorderStatus === "paused") {
        try {
          await wavRecorder.begin();
        } catch (e) {
          console.warn("Failed to begin wavRecorder for client_vad:", e);
        }
      }
      // Initialize MicVAD (RNNoise-based) to detect speech, we still use our WavRecorder to stream frames
      try {
        micVadRef.current = await MicVAD.new({
          positiveSpeechThreshold: 0.6,
          negativeSpeechThreshold: 0.25,
          redemptionMs: 2500,
          preSpeechPadMs: 800,
          minSpeechMs: 400,
          model: "v5",
          userSpeakingThreshold: 0.6,
          baseAssetPath: "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.29/dist/",
          startOnLoad: true,
          onnxWASMBasePath:
            "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.23.2/dist/",
          onSpeechStart: () => {
            vadSpeakingRef.current = true;
            attachPrerollNextFrameRef.current = true;
            // User just started speaking (handsfree)
            setInteractionState("userSpeaking");
            try { handleUserActivity(); } catch { }
          },
          onSpeechEnd: (_audio) => {
            // We ignore the provided audio buffer since we stream live frames
            vadSpeakingRef.current = false;
            try { setShowThinking(true); } catch { }
            attachPrerollNextFrameRef.current = false;
            if (speechEndTimerRef.current) {
              clearTimeout(speechEndTimerRef.current);
              speechEndTimerRef.current = null;
            }
            // User finished speaking (handsfree)
            setInteractionState("idle");
            try {
              // Only create a response if we actually streamed user audio this
              // turn AND Aris isn't still speaking / draining queued audio. This
              // stops a noise-triggered VAD turn from creating a second response
              // on top of one that's still playing.
              if (
                appendedSinceLastTurnRef.current &&
                !isBotSpeakingRef.current &&
                !pendingAudioTracksRef.current
              ) {
                client.createResponse();
              }
            } catch { }
            appendedSinceLastTurnRef.current = false;
          },
          onVADMisfire: () => {
            // False trigger detected; stop streaming and clear any pending buffers
            const hadAppended = !!appendedSinceLastTurnRef.current;
            vadSpeakingRef.current = false;
            attachPrerollNextFrameRef.current = false;
            appendedSinceLastTurnRef.current = false;
            preRollRef.current = [];
            preRollSamplesRef.current = 0;
            if (speechEndTimerRef.current) {
              clearTimeout(speechEndTimerRef.current);
              speechEndTimerRef.current = null;
            }
            // Treat as not speaking for UI
            setInteractionState("idle");
            // If we had appended audio already, treat misfire as an end-of-utterance and commit
            try {
              if (client && client.isConnected && client.isConnected() && hadAppended) {
                client.realtime?.send?.("input_audio_buffer.clear");
              }
            } catch (e) {
              console.warn("Failed to clear input audio buffer after VAD misfire:", e);
            }
          },
          // Optional debugging
          // onFrameProcessed: (prob) => {},
        });
        // Ensure VAD is actively listening
        try { await micVadRef.current.start?.(); } catch { }
      } catch (e) {
        console.warn("Failed to initialize @ricky0123/vad-web MicVAD:", e);
        micVadRef.current = null;
      }

      const appendPreRoll = () => {
        // Concatenate preRoll into a single Int16Array
        const total = preRollSamplesRef.current | 0;
        if (total <= 0) return;
        const merged = new Int16Array(total);
        let offset = 0;
        for (const chunk of preRollRef.current) {
          const typed = chunk instanceof Int16Array ? chunk : new Int16Array(chunk);
          merged.set(typed, offset);
          offset += typed.length;
        }
        preRollRef.current = [];
        preRollSamplesRef.current = 0;
        // const up = upsample16kTo24k(merged);
        try {
          if (client.isConnected && client.isConnected()) {
            client.appendInputAudio(merged);
            appendedSinceLastTurnRef.current = true;
          }
        } catch { }
      };

      const pushPreRoll = (frame) => {
        const typed = frame instanceof Int16Array ? frame : new Int16Array(frame);
        preRollRef.current.push(typed);
        preRollSamplesRef.current += typed.length;
        while (preRollSamplesRef.current > MAX_PREROLL_SAMPLES && preRollRef.current.length) {
          const first = preRollRef.current.shift();
          preRollSamplesRef.current -= first.length;
        }
      };

      await wavRecorder.record((data) => {
        if (!client.isConnected()) return;
        const frameAB = data.mono; // ArrayBuffer of PCM16
        const frame = frameAB instanceof Int16Array ? frameAB : new Int16Array(frameAB);
        // Any mic activity resets inactivity timer
        try { handleUserActivity(); } catch { }
        // While Aris is speaking OR still has audio queued/playing, do not stream
        // any mic audio to the backend. This prevents background noise (e.g. a TV)
        // during playback — including the trailing jitter buffer and the gaps
        // between multiple response items — from leaking in and triggering a new
        // response before the current one has fully finished playing.
        if (isBotSpeakingRef.current || pendingAudioTracksRef.current) {
          // Do not accumulate preroll or mark as user speech during bot speech
          preRollRef.current = [];
          preRollSamplesRef.current = 0;
          vadSpeakingRef.current = false;
          appendedSinceLastTurnRef.current = false;
          return;
        }
        const speaking = !!vadSpeakingRef.current;
        if (speaking) {
          if (attachPrerollNextFrameRef.current) {
            appendPreRoll();
            attachPrerollNextFrameRef.current = false;
          }
          // Send current voice frame
          try {
            // const up = upsample16kTo24k(frame);
            client.appendInputAudio(frame);
            appendedSinceLastTurnRef.current = true;
          } catch { }
        } else {
          // Not speaking
          // Maintain preroll buffer while quiet
          pushPreRoll(frame);
        }
      });
    }
  };

  // Pause/resume handlers for user-initiated pause
  const pauseSession = useCallback(() => {
    if (!isSessionActive) return;
    setIsPaused(true);
    setInteractionState("idle");
    // Pause Aris audio playback at the current position while keeping buffers intact
    try {
      const wavStreamPlayer = wavStreamPlayerRef.current;
      if (wavStreamPlayer && typeof wavStreamPlayer.pausePlayback === "function") {
        wavStreamPlayer.pausePlayback();
      }
    } catch { }
  }, [isSessionActive]);

  const resumeSession = useCallback(() => {
    if (!isSessionActive) return;
    setIsPaused(false);
    // Resume Aris audio playback from the last paused position
    try {
      const wavStreamPlayer = wavStreamPlayerRef.current;
      if (wavStreamPlayer && typeof wavStreamPlayer.resumePlayback === "function") {
        wavStreamPlayer.resumePlayback();
      }
    } catch { }
  }, [isSessionActive]);

  // Pause/resume client-side VAD while bot is speaking OR user paused to prevent echo-triggered misfires
  useEffect(() => {
    try {
      if (!isSessionActive || !handsFreeMode) return;
      if (!micVadRef.current) return;
      if (!clientVadModeRef.current) return;
      // Pause VAD when bot is speaking OR user manually paused
      if (isBotSpeaking || isPaused) {
        // Pause VAD to avoid echo-triggered misfires and suppress mic streaming
        micVadRef.current.pause && micVadRef.current.pause();
        // Reset local VAD state and preroll to ensure we don't leak frames
        vadSpeakingRef.current = false;
        appendedSinceLastTurnRef.current = false;
        preRollRef.current = [];
        preRollSamplesRef.current = 0;
      } else {
        micVadRef.current.start && micVadRef.current.start();
      }
    } catch { }
  }, [isBotSpeaking, isSessionActive, handsFreeMode, isPaused]);

  const disconnectConversation = useCallback(
    async (leave = false, status, route) => {
      setShowInactivityModal(false);
      // Stop mic and VAD immediately when disconnecting
      try { await stopClientAudio(); } catch { }
      setLoading(true);
      const mins = parseFloat(secondsRef.current / 60).toFixed(2);
      if (error === null) {
        const client = clientRef.current;
        const wavRecorder = wavRecorderRef.current;
        const wavStreamPlayer = wavStreamPlayerRef.current;
        try {
          await client.disconnect();
          // Note: wavRecorder cleanup is already handled by stopClientAudio() above
          // No need to call end() again as destroy() already released the mic
          wavStreamPlayer.interrupt();
          setArisDisconnected(true);

          // Activation completes on the closing turn, so by the time the member
          // hangs up the server has stamped it — this is the one moment worth
          // asking. Without it they walk away with a cached profile still
          // reading `activation_pending`. No-ops unless activation was pending;
          // deliberately not awaited, since the write is to localStorage and
          // outlives this screen.
          onActivationConversationEnd();

          // Reset all StatusMessage related states to clear the component
          setIsSessionActive(false);
          setIsCoachingActive(false);
          setError(null);
          setDisconnectText(null);
          setRetryNoticeText(null);
          setInteractionState("idle");
          setShowThinking(false);
          setShowConnectingText(false);

          // We don't need to call the end session session, becasue once the websocket is disconnect, we are summarize the sesion in the bacekend itself.
          // Only show toast if not already shown by handleStopSession
          if (route !== 'none') {
            showSuccessToast("Thank you, I will summarize this meeting and save it to your history");
          }
          if (handsFreeMode) {
            setHandsFreeMode(false);
          }
          if (leave && (route === "chat" || route === "dashboard" || route === "dialogues")) {
            returnToOrigin();
          }
          if (leave && route === "mins") {
            navigate("/manageMembership?showModal=true");
          }
          if (accessInitialized && route === "none" && !hasUnlimitedCoaching()) {
            setShowTimeoutText(
              "Your time is up. Please add more time to continue the session."
            );
          }
        } catch (error) {
          const message = error?.response?.data?.message;
          if (message === "No content from Aris") {
            // Reset all StatusMessage related states to clear the component
            setIsSessionActive(false);
            setIsCoachingActive(false);
            setError(null);
            setDisconnectText(null);
            setRetryNoticeText(null);
            setInteractionState("idle");
            setShowThinking(false);
            setShowConnectingText(false);

            // Only show toast if not already shown by handleStopSession
            if (route !== 'none') {
              showSuccessToast("Thank you, I will summarize this meeting and save it to your history");
            }
            setTag(null);
            // Note: wavRecorder cleanup is already handled by stopClientAudio() above
            if (handsFreeMode) {
              setHandsFreeMode(false);
            }
            if (leave && route === "chat") {
              returnToOrigin();
            }
            if (leave && route === "mins") {
              navigate("/manageMembership");
            }
            if (accessInitialized && route === "none" && !hasUnlimitedCoaching()) {
              setShowTimeoutText(
                "Your time is up. Please add more time to continue the session."
              );
            }
          } else {
            setError(
              "Error while ending the session. you're current session will be in progress"
            );
          }
        } finally {
          setLoading(false);
        }
      } else if (route === "mins") {
        setLoading(false);
        window.location.href = "/manageMembership";
      } else {
        setLoading(false);
        returnToOrigin();
      }
    },
    [error]
  );

  // One-shot entries — an Elements handoff, a dialogue handoff, or the Aris
  // tab's Voice choice (/dashboard?mode=voice, which renders us with autoStart)
  // — have no idle screen of their own to fall back to. Once the user has
  // actually started (and then ended) the session, send them back where they
  // came from rather than leaving them staring at the Coach's "Click the mic to
  // start" screen on a URL that says a session is running.
  const elementsHandoffStartedRef = useRef(false);
  useEffect(() => {
    if (!isElementsHandoff && !isDialogueHandoff && !isVoiceChosenHandoff && !autoStart) return;
    if (isSessionActive) {
      elementsHandoffStartedRef.current = true;
      return;
    }
    if (elementsHandoffStartedRef.current && arisDisconnected) {
      const t = setTimeout(() => {
        // An evolved Mission waits: the call is over, so open its Review.
        if (missionDraftIdRef.current) {
          navigate(missionRoutes.review(missionDraftIdRef.current), { replace: true });
        } else if ((isDialogueHandoff || isVoiceChosenHandoff) && window.history.length > 1) {
          navigate(-1);
        } else if (autoStart) {
          // Drop ?mode=voice so the Aris tab shows the chooser again. `replace`
          // keeps Back from re-entering the session that was just ended.
          navigate("/dashboard", { replace: true });
        } else {
          navigate("/dashboard");
        }
      }, 400);
      return () => clearTimeout(t);
    }
  }, [isElementsHandoff, isDialogueHandoff, isVoiceChosenHandoff, autoStart, isSessionActive, arisDisconnected, navigate]);

  const handleListClick = () => {
    setSidebarOpen(true);
  };

  const handleMicStart = async () => {
    // Only check time if user doesn't have unlimited coaching
    if (accessInitialized && typeof secondsRef.current === 'number' && secondsRef.current <= 0 && !hasUnlimitedCoaching()) {
      setShowTimeoutText(
        "Your time is up. Please add more time to continue the session."
      );
      clientRef.current.disconnect();
      // Use destroy() for forceful mic cleanup
      try {
        wavRecorderRef.current.destroy();
      } catch { }
    } else {
      // Add this check
      if (wavRecorderRef.current.getStatus() === 'recording') {
        return; // Already recording, do nothing
      }
      setRecordingMode(true);
      setInteractionState("userSpeaking");
      textRef.current = "";
      const client = clientRef.current;
      wavStreamPlayerRef.current.interrupt();
      try {
        await wavRecorderRef.current.record((data) => {
          if (!client.isConnected()) return;
          try {
            client.appendInputAudio(data.mono);
          } catch (e) {
            // ignore if disconnected mid-stream
          }
        });
      } catch (error) {
        console.error('Error starting recording:', error);
      }
    }
  };

  const handleMicStop = async () => {
    try {
      setRecordingMode(false);
      setInteractionState("idle");
      if (wavRecorderRef.current) {
        await wavRecorderRef.current.pause();
      }
      if (clientRef.current) {
        setShowThinking(true);
        clientRef.current.createResponse();
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
    }
  };

  // New session management functions
  const handleToggleSession = async () => {
    if (isSessionActive) {
      // Stop session
      await handleStopSession();
    } else {
      // Start session
      await handleStartSession();
    }
  };

  const handleStartSession = async () => {
    // Only check time if user doesn't have unlimited coaching
    if (accessInitialized && typeof secondsRef.current === 'number' && secondsRef.current <= 0 && !hasUnlimitedCoaching()) {
      setShowTimeoutText(
        "Your time is up. Please add more time to continue the session."
      );
      return;
    }

    try {
      // Normal voice session: clear any previous active topic context
      setActiveTopicSession(null);
      setIsSessionActive(true);
      setIsCoachingActive(true); // Update header context
      setShowConnectingText(true);
      setIsPaused(false);

      // Reset disconnected state to allow reconnection
      setArisDisconnected(false);

      // Clear any previous error states that might block reconnection
      setError(null);
      setLoading(false);

      // Clear any timeout refs that might interfere with reconnection
      if (retryNoticeTimerRef.current) {
        clearTimeout(retryNoticeTimerRef.current);
        retryNoticeTimerRef.current = null;
      }
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      if (inactivityModalTimeoutRef.current) {
        clearTimeout(inactivityModalTimeoutRef.current);
        inactivityModalTimeoutRef.current = null;
      }

      await connectConversation();
      // Start in handsfree mode by default
      setHandsFreeMode(true);
      // User is no longer first-time after starting a session
      setIsFirstTimeUser(false);
    } catch (error) {
      console.error('Error starting session:', error);
      setIsSessionActive(false);
      setIsCoachingActive(false);
      setError('Error starting session. Please try again.');
    }
  };

  const handleStopSession = async () => {
    try {
      // Proactively stop local audio capture and VAD before state changes
      try { await stopClientAudio(); } catch { }
      setIsSessionActive(false);
      setIsCoachingActive(false); // Update header context
      setHandsFreeMode(false);
      setIsPaused(false);
      // Clear any active topic session so banner does not persist
      setActiveTopicSession(null);
      setIsBotSpeaking(false); // Reset bot speaking state
      setShowThinking(false); // Reset thinking state
      setShowConnectingText(false); // Reset connecting state

      // Reset all StatusMessage related states to clear the component
      setError(null);
      setDisconnectText(null);
      setShowTimeoutText(null);
      setRetryNoticeText(null);
      setInteractionState("idle");

      // Don't call disconnectConversation with route 'none' to avoid duplicate toast
      // Just disconnect without showing toast here since we'll show it below
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      if (client && client.isConnected()) {
        await client.disconnect();
      }
      // Note: wavRecorder cleanup is already handled by stopClientAudio() above
      // No need to call end() again as destroy() already released the mic
      if (wavStreamPlayer) {
        wavStreamPlayer.interrupt();
      }

      setArisDisconnected(true);
      // Show single toast message
      showSuccessToast('Thank you, I will summarize this meeting and save it to your history');
    } catch (error) {
      console.error('Error stopping session:', error);
    }
  };

  const handleOpenHistory = () => {
    setShowVoiceHistory(true);
  };

  const handleCloseHistory = () => {
    setShowVoiceHistory(false);
  };

  const handleOpenMinutesModal = () => {
    setShowMinutesModal(true);
  };

  const handleCloseMinutesModal = () => {
    setShowMinutesModal(false);
  };

  const handleOpenKeyboardSwitchModal = () => {
    // Only show modal during active session, otherwise direct switch
    if (isSessionActive) {
      setShowKeyboardSwitchModal(true);
    } else {
      handleDirectSwitchToKeyboard();
    }
  };

  const handleDirectSwitchToKeyboard = async () => {
    try {
      // Always update the coaching mode when switching
      await updateCoachingMode('keyboard');
      localStorage.setItem('coaching_mode', 'keyboard');

      // Refetch profile to get updated data
      try {
        const updatedProfile = await getUserProfile();
        localStorage.setItem('profile', JSON.stringify(updatedProfile));
      } catch (profileError) {
        console.error('Failed to refetch profile:', profileError);
        // Fallback: update local profile object
        try {
          const profileData = JSON.parse(localStorage.getItem('profile') || '{}');
          if (profileData.meta_info && profileData.meta_info.settings) {
            profileData.meta_info.settings.coaching_mode = 'keyboard';
          } else {
            // Create the structure if it doesn't exist
            if (!profileData.meta_info) profileData.meta_info = {};
            if (!profileData.meta_info.settings) profileData.meta_info.settings = {};
            profileData.meta_info.settings.coaching_mode = 'keyboard';
          }
          profileData.coaching_mode = 'keyboard';
          localStorage.setItem('profile', JSON.stringify(profileData));
        } catch (_) { }
      }
    } catch (_) { }
    // Force page reload to ensure Dashboard picks up the new coaching mode
    window.location.href = '/dashboard';
  };

  const handleCloseKeyboardSwitchModal = () => {
    setShowKeyboardSwitchModal(false);
  };


  const handleShowLeaveSessionModal = () => {
    if (isSessionActive) {
      setShowLeaveSessionModal(true);
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handleCloseLeaveSessionModal = () => {
    setShowLeaveSessionModal(false);
  };

  const handleConfirmLeaveSession = async () => {
    await handleStopSession();
    setShowLeaveSessionModal(false);
    window.location.href = '/dashboard';
  };

  const handleLeave = () => {
    if (arisDisconnected || showTimeoutText) {
      window.location.href = "/dashboard";
    } else {
      setShowExitModal(false);
      handleShowLeaveSessionModal();
    }
  };

  // Coaching Topics handlers
  const handleOpenCoachingTopics = () => {
    setTopicsSlideFromLeft(false);
    setShowCoachingTopicsModal(true);
  };

  const handleOpenYourElements = () => {
    setShowCoachingTopicsModal(false);
    setShowYourElementsModal(true);
  };

  const handleCloseYourElements = () => {
    setShowYourElementsModal(false);
  };

  const handleTopicNavigationAction = (_topic, target) => {
    if (target?.kind === "internal_route" && target.value === "/elements") {
      handleOpenYourElements();
      return;
    }
    if (target?.kind === "external_url" && target.value) {
      window.open(target.value, "_blank", "noopener,noreferrer");
      return;
    }
    console.warn("[Coach] unsupported topic navigation target", target);
  };

  const handleCloseCoachingTopics = () => {
    setShowCoachingTopicsModal(false);
    setTopicsSlideFromLeft(false);
  };

  const handleSelectTopic = (topic) => {
    setSelectedTopic(topic);
    setShowCoachingTopicsModal(false);
    setShowTopicOptionsModal(true);
  };

  const handleCloseTopicOptions = () => {
    setShowTopicOptionsModal(false);
    setSelectedTopic(null);
  };

  const handleBackToTopics = () => {
    setShowTopicOptionsModal(false);
    setSelectedTopic(null);
    setTopicsSlideFromLeft(true);
    setShowCoachingTopicsModal(true);
  };

  // Auto-start for the Aris tab's Voice choice.
  //
  // Gated on accessInitialized because handleStartSession only enforces the
  // out-of-minutes check once entitlements have resolved — starting earlier
  // would open a session for a member with no time left. The ref makes it
  // once-only, so a re-render (or ending the session) cannot silently reopen it.
  //
  // Also gated on clientReady, which is the whole point of this guard: arriving
  // here by client-side navigation (the chooser on /dashboard) means
  // FunctionalityAccessProvider resolved long ago, so accessInitialized is
  // already true on the first commit — we would call connectConversation while
  // handleInstructions is still awaiting getUserFaith and clientRef.current is
  // null, which surfaces as "Error connecting to Aris". A hard refresh happened
  // to work only because access had to initialize first, buying the client
  // enough time to be built.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if ((!autoStart && !isVoiceChosenHandoff) || autoStartedRef.current || !accessInitialized || !clientReady) return;
    autoStartedRef.current = true;
    handleStartSession();
    // handleStartSession is intentionally omitted: it is redefined every render,
    // and the ref already guarantees this fires once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, isVoiceChosenHandoff, accessInitialized, clientReady]);

  const handleStartTopicSession = async (sessionDescriptor) => {
    setShowTopicOptionsModal(false);
    setActiveTopicSession(sessionDescriptor);

    // Start the voice session with the topic context
    // Only check time if user doesn't have unlimited coaching
    if (accessInitialized && typeof secondsRef.current === 'number' && secondsRef.current <= 0 && !hasUnlimitedCoaching()) {
      setShowTimeoutText(
        "Your time is up. Please add more time to continue the session."
      );
      return;
    }

    try {
      setIsSessionActive(true);
      setIsCoachingActive(true);
      setShowConnectingText(true);
      setIsPaused(false);
      setArisDisconnected(false);
      setError(null);
      setLoading(false);

      // Clear any timeout refs
      if (retryNoticeTimerRef.current) {
        clearTimeout(retryNoticeTimerRef.current);
        retryNoticeTimerRef.current = null;
      }
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      if (inactivityModalTimeoutRef.current) {
        clearTimeout(inactivityModalTimeoutRef.current);
        inactivityModalTimeoutRef.current = null;
      }

      // Set topic_id and option_id on the client before connecting for quick coaching topics.
      // Never for a member mid-activation — the entry CTA is hidden for them, and
      // applyActivationToClient has already claimed the dialog type.
      if (clientRef.current && !isActivationSession) {
        if (sessionDescriptor.topicId) {
          clientRef.current.setTopicId(sessionDescriptor.topicId);
        }
        if (sessionDescriptor.optionId) {
          clientRef.current.setOptionId(sessionDescriptor.optionId);
        }
      }
      await connectConversation();
      setHandsFreeMode(true);
      setIsFirstTimeUser(false);
    } catch (error) {
      console.error('Error starting topic session:', error);
      setIsSessionActive(false);
      setIsCoachingActive(false);
      setError('Error starting session. Please try again.');
      setActiveTopicSession(null);
    }
  };

  // Navigation handled via checkActivationComplete() for normal dialog flow only
  return (
    <div className="coach-banner">
      {/* Modals */}
      {showSummaryModal && !isMobile && (
        <ChatModal
          isOpen={showSummaryModal}
          onClose={() => setShowSummaryModal(false)}
          children={
            <SummaryModal
              onClose={() => setShowSummaryModal(false)}
              summaryData={endSessionData}
            />
          }
          height={"fit-content"}
          width={"476px"}
        />
      )}
      {showSummaryModal && isMobile && (
        <MobilePopUp
          isPopupVisible={showSummaryModal}
          onClose={() => setShowSummaryModal(false)}
          children={
            <SummaryModal
              hideClose={true}
              onClose={() => setShowSummaryModal(false)}
              summaryData={endSessionData}
            />
          }
          hideClose={false}
        />
      )}
      {showActionPlanModal && !isMobile && (
        <ChatModal
          isOpen={showActionPlanModal}
          onClose={() => setShowActionPlanModal(false)}
          children={
            <ActionPlanModal
              onClose={() => setShowActionPlanModal(false)}
              summaryData={endSessionData}
            />
          }
          height={"fit-content"}
          width={"476px"}
        />
      )}
      {showActionPlanModal && isMobile && (
        <MobilePopUp
          isPopupVisible={showActionPlanModal}
          onClose={() => setShowActionPlanModal(false)}
          children={
            <ActionPlanModal
              hideClose={true}
              isMobile={isMobile}
              onClose={() => setShowActionPlanModal(false)}
              summaryData={endSessionData}
            />
          }
          hideClose={false}
        />
      )}
      {showSmartHabitModal && !isMobile && (
        <ChatModal
          isOpen={showSmartHabitModal}
          onClose={() => setShowSmartHabitModal(false)}
          children={
            <SmartHabit
              onClose={() => setShowSmartHabitModal(false)}
              summaryData={endSessionData}
            />
          }
          height={"fit-content"}
          width={"476px"}
        />
      )}
      {showActionPlanModal && isMobile && (
        <MobilePopUp
          isPopupVisible={showSmartHabitModal}
          onClose={() => setShowSmartHabitModal(false)}
          children={
            <SmartHabit
              hideClose={true}
              isMobile={isMobile}
              onClose={() => setShowSmartHabitModal(false)}
              summaryData={endSessionData}
            />
          }
          hideClose={false}
        />
      )}

      {/* Sidebar */}
      {sidebarOpen && screenWidth > 480 && (
        <>
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
          <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>
            <History
              onClose={() => setSidebarOpen(false)}
              sessionID={sessionIdRef.current}
              client={clientRef.current}
              wavRecorder={wavRecorderRef.current}
            />
          </div>
        </>
      )}

      {sidebarOpen && screenWidth <= 480 && (
        <MobilePopUp
          isPopupVisible={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          children={
            <History
              onClose={() => setSidebarOpen(false)}
              sessionID={sessionIdRef.current}
              client={clientRef.current}
              wavRecorder={wavRecorderRef.current}
            />
          }
          hideClose={true}
        />
      )}

      <InactivityModal
        showInactivityModal={showInactivityModal}
        setShowInactivityModal={setShowInactivityModal}
        disconnectConversation={disconnectConversation}
        arisDisconnected={arisDisconnected}
        navigate={navigate}
      />

      <ExitModal
        showExitModal={showExitModal}
        setShowExitModal={setShowExitModal}
        handleLeave={handleLeave}
      />

      {/* Voice Chat History Modal */}
      {showVoiceHistory && (
        <>
          {screenWidth > 480 ? (
            <div className="coaching-history-modal-overlay">
              <div className="coaching-history-modal-content">
                <VoiceChatHistory
                  historyMenuClose={handleCloseHistory}
                  onHistorySessionClick={(item) => {
                    try {
                      const id = item?.coaching_id || item?.session_id || item?.sessionId || item?.id;
                      if (id) {
                        navigate(`/dialogues?sessionId=${encodeURIComponent(id)}`);
                      }
                    } finally {
                      handleCloseHistory();
                    }
                  }}
                />
              </div>
            </div>
          ) : (
            <MobilePopUp
              isPopupVisible={showVoiceHistory}
              onClose={handleCloseHistory}
              hideClose={true}
              height="90dvh"
            >
              <VoiceChatHistory
                historyMenuClose={handleCloseHistory}
                onHistorySessionClick={(item) => {
                  try {
                    const id = item?.coaching_id || item?.session_id || item?.sessionId || item?.id;
                    if (id) {
                      navigate(`/dialogues?sessionId=${encodeURIComponent(id)}`);
                    }
                  } finally {
                    handleCloseHistory();
                  }
                }}
              />
            </MobilePopUp>
          )}
        </>
      )}

      {/* Request Coaching Minutes Modal */}
      {showMinutesModal && (
        <>
          {screenWidth > 480 ? (
            <ChatModal
              isOpen={showMinutesModal}
              onClose={handleCloseMinutesModal}
              width="462px"
              height="auto"
            >
              <RequestCoachingMinutes
                isOpen={showMinutesModal}
                onClose={handleCloseMinutesModal}
                remainingMinutes={Math.floor(secondsRef.current / 60)}
                redirectUrl="/dashboard"
                onSuccess={() => {
                  // Refresh the session data when minutes are purchased
                  window.location.reload();
                }}
              />
            </ChatModal>
          ) : (
            <MobilePopUp
              isPopupVisible={showMinutesModal}
              onClose={handleCloseMinutesModal}
              hideClose={false}
            >
              <RequestCoachingMinutes
                isOpen={showMinutesModal}
                onClose={handleCloseMinutesModal}
                remainingMinutes={Math.floor(secondsRef.current / 60)}
                redirectUrl="/dashboard"
                onSuccess={() => {
                  // Refresh the session data when minutes are purchased
                  window.location.reload();
                }}
              />
            </MobilePopUp>
          )}
        </>
      )}

      {/* Voice Coaching Tour */}
      <ArisTour
        id="voice"
        steps={[
          {
            selector: '.coaching-mic-container',
            content: (
              <div>
                <h3>Start voice dialogue session with Aris</h3>
                <p>Click the microphone to begin your voice dialogue session. Aris will listen and respond to help guide you.</p>
              </div>
            ),
            placement: 'top'
          },
          {
            selector: '.coaching-button-container',
            content: (
              <div>
                <h3>Switch to keyboard mode at any time.</h3>
                <p>Prefer typing? Click here to switch to keyboard mode and continue your conversation with Aris via text.</p>
              </div>
            ),
            placement: 'bottom'
          },
          {
            selector: '.your-elements-btn',
            content: (
              <div>
                <p>Explore <strong>Your Elements</strong> — your personal Periodic Table of the Self.</p>
              </div>
            ),
            placement: 'bottom'
          },
          {
            selector: '.quick-session-topics-btn',
            content: (
              <div>
                <p>Access suggested <strong>dialogues sessions.</strong></p>
              </div>
            ),
            placement: 'bottom'
          },
          {
            selector: '.coaching-video-container-below',
            content: (
              <div>
                <p>If you ever need to interrupt Aris, just say <strong>“Hey Aris”</strong>.</p>
              </div>
            ),
            placement: 'bottom'
          },
          {
            selector: '.header-menu-icon-container',
            content: (
              <div>
                <p>Your <strong>session history </strong>is in the main menu.</p>
              </div>
            ),
            placement: 'bottom'
          },


        ]}
        routeMatch={(pathname) => pathname === '/dashboard'}
        readySelectors={['.coaching-hands-free-container', '.coaching-mic-container', '.coaching-keyboard-session-container']}
        primaryColor="#1E69FF"
        zIndex={10001}
        spotlightRadiusMap={{
          '.coaching-hands-free-container': 44,
          '.coaching-mic-container': 44,
          '.coaching-keyboard-session-container': 44,
        }}
        defaultSpotlightRadius={44}
      />

      {/* Switch to Keyboard Mode modal */}
      <GenericModal
        isOpen={showKeyboardSwitchModal}
        onClose={() => setShowKeyboardSwitchModal(false)}
        heading="Switch to Keyboard Mode?"
        primaryLabel="Switch"
        secondaryLabel="Cancel"
        onPrimary={async () => {
          try {
            // Always update the coaching mode when switching
            await updateCoachingMode('keyboard');
            localStorage.setItem('coaching_mode', 'keyboard');

            // Refetch profile to get updated data
            try {
              const updatedProfile = await getUserProfile();
              localStorage.setItem('profile', JSON.stringify(updatedProfile));
            } catch (profileError) {
              console.error('Failed to refetch profile:', profileError);
              // Fallback: update local profile object
              try {
                const profileData = JSON.parse(localStorage.getItem('profile') || '{}');
                if (profileData.meta_info && profileData.meta_info.settings) {
                  profileData.meta_info.settings.coaching_mode = 'keyboard';
                } else {
                  // Create the structure if it doesn't exist
                  if (!profileData.meta_info) profileData.meta_info = {};
                  if (!profileData.meta_info.settings) profileData.meta_info.settings = {};
                  profileData.meta_info.settings.coaching_mode = 'keyboard';
                }
                profileData.coaching_mode = 'keyboard';
                localStorage.setItem('profile', JSON.stringify(profileData));
              } catch (_) { }
            }
          } catch (_) { }
          setShowKeyboardSwitchModal(false);
          // Force page reload to ensure Dashboard picks up the new coaching mode
          window.location.href = '/dashboard';
        }}
        onSecondary={() => setShowKeyboardSwitchModal(false)}
        maxWidth="548px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0 }}>
            By switching you will start a new chat, but Aris will remember everything you talked about.
          </p>
        </div>
      </GenericModal>

      {/* Leave Session Modal */}
      {showLeaveSessionModal && (
        <>
          {screenWidth > 480 ? (
            <ChatModal
              isOpen={showLeaveSessionModal}
              onClose={handleCloseLeaveSessionModal}
              width="400px"
              height="auto"
            >
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>End Coaching Session?</h2>
                <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: '1.5' }}>
                  Are you sure you want to end your current coaching session?
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={handleConfirmLeaveSession}
                    style={{
                      backgroundColor: '#ff4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    End Session
                  </button>
                  <button
                    onClick={handleCloseLeaveSessionModal}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </ChatModal>
          ) : (
            <MobilePopUp
              isPopupVisible={showLeaveSessionModal}
              onClose={handleCloseLeaveSessionModal}
              hideClose={false}
            >
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>End Coaching Session?</h2>
                <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: '1.5' }}>
                  Are you sure you want to end your current coaching session?
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={handleConfirmLeaveSession}
                    style={{
                      backgroundColor: '#ff4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    End Session
                  </button>
                  <button
                    onClick={handleCloseLeaveSessionModal}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </MobilePopUp>
          )}
        </>
      )}

      {/* Coaching Topics Modal */}
      <CoachingTopicsModal
        isOpen={showCoachingTopicsModal}
        onClose={handleCloseCoachingTopics}
        onSelectTopic={handleSelectTopic}
        onNavigationAction={handleTopicNavigationAction}
        platform="web"
        sessionType="voice"
        slideFromLeft={topicsSlideFromLeft}
      />

      {/* Topic Options Modal */}
      <TopicOptionsModal
        isOpen={showTopicOptionsModal}
        onClose={handleCloseTopicOptions}
        topic={selectedTopic}
        platform="web"
        sessionType="voice"
        onStartSession={handleStartTopicSession}
        onBack={handleBackToTopics}
      />

      {/* Your Elements Modal */}
      <YourElementsModal
        isOpen={showYourElementsModal}
        onClose={handleCloseYourElements}
      />

      {loading ? (
        <DefaultLoader />
      ) : (
        <>

          {/* Out of minutes UI - only show if user doesn't have unlimited */}
          {accessInitialized && typeof secondsRef.current === 'number' && secondsRef.current <= 0 && !hasUnlimitedCoaching() ? (
            <div className="coaching-main-container">
              <VideoDisplay interactionState={"idle"} />
              <div className="coaching-main-container-text" style={{ marginTop: '40px' }}>
                <div className="coaching-llm-text-container">
                  <p className="coaching-error-message" style={{ fontSize: '18px', fontWeight: '500', marginBottom: '24px' }}>
                    I'm sorry. You're all out of voice session minutes.
                  </p>
                  <button
                    className="coaching-add-minutes-button"
                    onClick={handleOpenMinutesModal}
                  >
                    Add More Minutes
                  </button>
                </div>
              </div>
              <SpeechControls
                recordingMode={false}
                handsFreeMode={false}
                arisDisconnected={true}
                error={null}
                secondsRef={secondsRef}
                handleMicStart={() => { }}
                handleMicStop={() => { }}
                setHandsFreeMode={() => { }}
                disconnectConversation={disconnectConversation}
                disconnectText={null}
                timeoutText={null}
                onKeyboardSwitchClick={handleOpenKeyboardSwitchModal}
                onOpenHistory={() => { }}
                onToggleSession={() => { }}
                isSessionActive={false}
                onOpenMinutesModal={handleOpenMinutesModal}
                isGroup={isGroupCoaching}
                partnerName={groupPartnerName}
              />
            </div>
          ) : (
            <div className="coaching-main-container">
              {/* Active Topic Session Indicator - placed after video for flex layout */}
              {isSessionActive && activeTopicSession && (() => {
                const TopicIcon = getTopicIcon(activeTopicSession.categoryName, activeTopicSession.topicTitle);
                return (
                  <div className="active-topic-indicator">
                    <span className="active-topic-icon">
                      <TopicIcon />
                    </span>
                    <span className="active-topic-title">{activeTopicSession.topicTitle}</span>
                  </div>
                );
              })()}
              {isSessionActive && missionDraftReady && (
                <div className="active-topic-indicator" role="status">
                  Your evolved Mission is ready. It opens for review when you end the call.
                </div>
              )}
              <div className="coaching-video-welcome-container">
                <VideoDisplay interactionState={interactionState} />
                <div className="coaching-main-container-text">
                  <StatusMessage
                    error={error}
                    disconnectText={disconnectText}
                    showConnectingText={showConnectingText && isSessionActive}
                    showTimeoutText={showTimeoutText}
                    retryNoticeText={retryNoticeText}
                    thinking={showThinking}
                    thinkingText={"Aris is thinking"}
                    isTalking={isBotSpeaking && !isPaused}
                    isListening={interactionState === "userSpeaking" && handsFreeMode}
                    isPaused={isPaused}
                    idleMessage={
                      !isSessionActive
                        ? (
                          isGroupCoaching
                            ? (
                              groupFirstName && groupPartnerName
                                ? `Welcome ${groupFirstName} & ${groupPartnerName}`
                                : (isFirstTimeUser ? welcomeMessage : "Click the mic to start your coaching session")
                            )
                            : isDialogueHandoff
                              ? "Click the mic to begin this dialogue with Aris"
                              : isCheckInHandoff
                              ? "Click the mic to start today's check-in with Aris"
                              : (isFirstTimeUser ? welcomeMessage : "Click the mic to start your coaching session")
                        )
                        : (interactionState === "idle" && !showThinking ? "Aris is ready to listen" : null)
                    }
                  />
                </div>

                {/* Low Minutes Warning - only show if user doesn't have unlimited */}
                {!hasUnlimitedCoaching() && secondsRef.current > 0 && secondsRef.current <= 300 && ( // Show when 5 minutes or less
                  <div className="coaching-low-minutes-warning">
                    <p className="coaching-low-minutes-text">
                      You have {Math.floor(secondsRef.current / 60)} minutes remaining
                    </p>
                    <button
                      className="coaching-add-minutes-button coaching-add-minutes-small"
                      onClick={handleOpenMinutesModal}
                    >
                      Add Minutes
                    </button>
                  </div>
                )}

                <TagButton
                  disconnectText={disconnectText}
                  tag={tag}
                  showExitModal={showExitModal}
                  sidebarOpen={sidebarOpen}
                  showSummaryModal={showSummaryModal}
                  showActionPlanModal={showActionPlanModal}
                  showSmartHabitModal={showSmartHabitModal}
                  setShowSummaryModal={setShowSummaryModal}
                  setShowActionPlanModal={setShowActionPlanModal}
                  setShowSmartHabitModal={setShowSmartHabitModal}
                />
              </div>
              <SpeechControls
                recordingMode={recordingMode}
                handsFreeMode={handsFreeMode}
                arisDisconnected={arisDisconnected}
                error={error}
                secondsRef={secondsRef}
                handleMicStart={handleMicStart}
                handleMicStop={handleMicStop}
                setHandsFreeMode={setHandsFreeMode}
                disconnectConversation={disconnectConversation}
                disconnectText={disconnectText}
                timeoutText={showTimeoutText}
                onToggleSession={handleToggleSession}
                showEndSessionButton={isSessionActive}
                isSessionActive={isSessionActive}
                onOpenMinutesModal={handleOpenMinutesModal}
                onKeyboardSwitchClick={handleOpenKeyboardSwitchModal}
                isPaused={isPaused}
                onPause={pauseSession}
                onResume={resumeSession}
                // Group coaching context
                isGroup={isGroupCoaching}
                partnerName={groupPartnerName}
              />

              {/* Quick Coaching Topics Button - only show when session is not active.
                  Withheld while activation is pending: the member's one job on this
                  screen is the activation conversation, and Elements/Topics are
                  withheld everywhere else too (HomeTabBar, ActivationGatedRoute). */}
              {!isSessionActive && !isActivationSession && !isElementsHandoff && !isResumeHandoff && !isDialogueHandoff && !isVoiceChosenHandoff && (
                <div className="quick-actions-row">
                  <YourElementsEntryButton
                    onClick={handleOpenYourElements}
                    sessionType="voice"
                  />
                  <button
                    className="quick-coaching-topics-btn quick-session-topics-btn"
                    onClick={handleOpenCoachingTopics}
                  >
                    Quick Session Topics
                  </button>
                </div>
              )}

              {/* Resume/dialogue handoffs: replace the entry CTAs with a back button */}
              {!isSessionActive && (isResumeHandoff || isDialogueHandoff || isVoiceChosenHandoff) && (
                <div className="quick-actions-row">
                  <button
                    type="button"
                    className="coach-resume-back-btn"
                    onClick={() =>
                      isDialogueHandoff || isVoiceChosenHandoff ? returnToOrigin() : navigate("/dashboard")
                    }
                  >
                    <BackArrowIcon />
                    {isDialogueHandoff || isVoiceChosenHandoff ? "Back" : "Back to home"}
                  </button>
                </div>
              )}
            </div>
          )}
          <Tooltip
            anchorSelect="#tooltip-anchor"
            place={screenWidth <= 488 ? "top-end" : "top"}
            border={"2px solid #D9D9D9"}
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              color: "#000000",
              zIndex: 100,
              display: !handsFreeMode ? "block" : "none",
            }}
          >
            <p
              style={{ textAlign: "center", fontWeight: 600, fontSize: "16px" }}
            >
              Past dialogue session
            </p>
            <p
              style={{
                textAlign: "center",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "14.4px",
              }}
            >
              View your past dialogue session anytime.
            </p>
          </Tooltip>
        </>
      )}
    </div>
  );
}

export default ArisCoach;